import React from "react";
import PropTypes from "prop-types";
import { FormGroup } from "react-bootstrap-3";
import styled from "styled-components";
import ArrowDown from "../../icons/arrowDown";
import { defineMessages, FormattedMessage } from "react-intl";
import globalMessages from "../../intl/globalMessages";

const messages = defineMessages({
  ...globalMessages,
  resetAllFilters: {
    id: "app.smartSelector.resetAllFilters",
    description: "Reset All Filters",
    defaultMessage: "Reset All Filters",
  },
});

const StyledArrowDown = styled(ArrowDown)`
  height: 24px;
  width: 24px;
  ${props => (props.flip ? `transform: rotate(0.5turn)` : undefined)};
`;
const FilterContainer = styled.div`
  display: flex;
  position: absolute;
  width: 90%;
  z-index: 2;
  max-height: 35px;
  // Prevents selection of filter elements on double click
  * {
    -moz-user-select: none;
    -webkit-user-select: none;
    -ms-user-select: none;
    user-select: none;
  }
`;

const StyledCollapseContainer = styled.span`
  width: 100%;
  background-color: ${({ theme }) => theme.greyBackground};;
  border-radius: 4px;
  border: 1px solid ${props => (props.open ? `#3675b4` : `#dae6e8`)};
  margin: 0 5px;
  padding: 0 5px;
  max-height: ${props => (props.open ? `260px` : `35px`)};
  margin-bottom: auto;
`;

const CollapseHeader = styled.div`
  text-indent: 5px;
  height: 35px;
  word-wrap: break-word;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const CollapseBody = styled.div`
  display: ${props => (props.open ? `block` : `none`)};
  max-height: 215px;
  height: 100%;
  & *:first-child {
    margin-top: 3px;
  }
`;

const CollapseListWrapper = styled.div`
  overflow: auto;
  max-height: 170px;
  margin-bottom: 5px;
`;

const CheckAllControl = styled.div`
  margin-left: 5px;
  margin-bottom: 10px;
  padding-top: 60px;
  color: #87cefa;
  cursor: default;
  & *:first-child {
    margin-right: 5px;
    cursor: pointer;
  }
  & *:last-child {
    margin-left: 5px;
    cursor: pointer;
  }
`;

const CheckAllControlFilter = styled(CheckAllControl)`
  padding-top: 0;
`;

const ResetFilters = styled.div`
  margin-left: 5px;
  margin-bottom: 5px;
  color: #87cefa;
  & > * {
    cursor: pointer;
  }
`;

const StyledDiv = styled.div`
  margin-bottom: 0;
  padding-bottom: 0;
  border-bottom: 0;
`;

const ComponentContainer = styled.div`
  margin-bottom: 20px;
  ${props => (props.disabled ? `pointer-events: none;` : ``)}
`;

const StyledLabel = styled.label`
  display: flex;
  align-items: center;
`;

function applyStaticFilters(filter, list, staticFilters) {
  let localFilter = {
    ...filter,
  };

  Object.keys(localFilter).forEach(
    key =>
      (localFilter[key] = localFilter[key].filter(
        option =>
          !Object.keys(option.filters).some(
            filter =>
              staticFilters[filter] && staticFilters[filter].includes(option.filters[filter].ticker)
          )
      ))
  );

  const localList = list.filter(
    option =>
      !Object.keys(option.filters).some(
        filter =>
          staticFilters[filter] && staticFilters[filter].includes(option.filters[filter].ticker)
      )
  );

  return { list: localList, filter: localFilter };
}

function applyFilters(filter, list, filtersApplied) {
  let localFilter = { ...filter };

  Object.keys(localFilter).forEach(
    key =>
      (localFilter[key] = localFilter[key].filter(
        option =>
          // If it's in filtersApplied, it doesn't filter it out so the checkbox won't disappear
          (filtersApplied[key] &&
            filtersApplied[key].some(ticker => ticker === option.filters[key].ticker)) ||
          // Filters out the option if any of its filter's tickers is in filtersApplied
          !Object.keys(option.filters).some(
            filter =>
              filtersApplied[filter] &&
              filtersApplied[filter].some(ticker => ticker === option.filters[filter].ticker)
          )
      ))
  );
  // Filters out the option if any of its filter's tickers is in filtersApplied
  let localList = list.filter(
    option =>
      !Object.keys(option.filters).some(
        filter =>
          filtersApplied[filter] &&
          filtersApplied[filter].some(ticker => ticker === option.filters[filter].ticker)
      )
  );

  return { filter: localFilter, list: localList };
}

function removeValuesHiddenByStaticFilters(originalOptions, staticFilters, value, onChange, field) {
  // Removes options hidden by static filters from value
  let filteredOutTickers = originalOptions
    .filter(option =>
      Object.keys(option.filters).some(
        filter =>
          staticFilters[filter] && staticFilters[filter].includes(option.filters[filter].ticker)
      )
    )
    .map(option => option.ticker);

  const localValue =
    Array.isArray(value) && value.filter(ticker => !filteredOutTickers.includes(ticker));
  onChange({ value: localValue, field });
}

function filterOutDuplicateOptions(options, type) {
  return options[type].reduce((accum, curr) => {
    // Filters out duplicate options
    !accum.some(option => option.filters[type].ticker === curr.filters[type].ticker) &&
      accum.push(curr);
    return accum;
  }, []);
}

class SmartSelector extends React.Component {
  originalListOptions = [...this.props.field.options];
  // Creates an object with filter names as parameters and the entire array of option objects as value for each parameter
  originalFilterOptions =
    this.props.field.options[0] &&
    this.props.field.options[0].filters &&
    Object.keys(this.props.field.options[0].filters).reduce(
      (accum, it) => ({ ...accum, [it]: this.props.field.options }),
      {}
    );

  constructor(props) {
    super(props);
    this.state = {
      staticFilters: this.props.field.staticFilters,
      filtersApplied: {},
      open: "",
    };

    const optionsAfterStaticFilters = applyStaticFilters(
      this.originalFilterOptions,
      this.originalListOptions,
      this.state.staticFilters
    );

    this.state.optionsAfterStaticFilters = optionsAfterStaticFilters.list;
    this.state.filterOptionsAfterStaticFilter = optionsAfterStaticFilters.filter;
    this.state.filterOptions = optionsAfterStaticFilters.filter;
    this.state.listOptions = optionsAfterStaticFilters.list;

    removeValuesHiddenByStaticFilters(
      this.originalListOptions,
      this.state.staticFilters,
      this.props.value,
      this.props.onChange,
      this.props.field.fieldName
    );
  }

  UNSAFE_componentWillReceiveProps(nextProps, nextContext) {
    //TODO the obj that comes is exactly the same but the ref seems to be different, shouldn't react be doing that?
    // Deep comparison is needed since staticFilters is an object. JSON.stringify method should be OK in this case.
    if (
      JSON.stringify(this.props.field.staticFilters) !==
      JSON.stringify(nextProps.field.staticFilters)
    ) {
      const optionsAfterStaticFilters = applyStaticFilters(
        this.originalFilterOptions,
        this.originalListOptions,
        nextProps.field.staticFilters
      );
      const filtersApplied = {};
      const options = applyFilters(
        optionsAfterStaticFilters.filter,
        optionsAfterStaticFilters.list,
        filtersApplied
      );

      this.setState({
        staticFilters: nextProps.field.staticFilters,
        filterOptionsAfterStaticFilter: optionsAfterStaticFilters.filter,
        optionsAfterStaticFilters: optionsAfterStaticFilters.list,
        filtersApplied,
        listOptions: options.list,
        filterOptions: options.filter,
      });

      removeValuesHiddenByStaticFilters(
        this.originalListOptions,
        nextProps.field.staticFilters,
        this.props.value,
        this.props.onChange,
        this.props.field.fieldName
      );
    }
  }

  handleFilterChange = (event, filterType, ticker) => {
    let filtersApplied = { ...this.state.filtersApplied };

    // Adds the ticker that is being selected to the list of applied filters if it isn't added already. The list has filter names as properties and arrays of string tickers as values.
    // Deletes the ticker from the list if the checkbox is checked again
    if (event.target.checked === false) {
      if (filtersApplied[filterType] && !filtersApplied[filterType].includes(ticker)) {
        filtersApplied[filterType].push(ticker);
      } else {
        filtersApplied[filterType] = [];
        filtersApplied[filterType].push(ticker);
      }
    } else {
      if (filtersApplied[filterType]) {
        filtersApplied[filterType].splice(
          filtersApplied[filterType].findIndex(it => it === ticker),
          1
        );
      }
    }
    const options = applyFilters(
      this.state.filterOptionsAfterStaticFilter,
      this.state.optionsAfterStaticFilters,
      filtersApplied
    );
    this.setState({
      filtersApplied,
      listOptions: options.list,
      filterOptions: options.filter,
    });
  };

  handleAllFiltersChange = (action, filterType) => {
    let filtersApplied = { ...this.state.filtersApplied };

    filtersApplied[filterType] =
      action === "select"
        ? []
        : filterOutDuplicateOptions(this.state.filterOptions, filterType)
            .map(option => option.filters[filterType])
            .map(it => it.ticker);
    const options = applyFilters(
      this.state.filterOptionsAfterStaticFilter,
      this.state.optionsAfterStaticFilters,
      filtersApplied
    );
    this.setState({
      filtersApplied,
      listOptions: options.list,
      filterOptions: options.filter,
    });
  };

  handleResetAllFilters = () => {
    const options = applyFilters(
      this.state.filterOptionsAfterStaticFilter,
      this.state.optionsAfterStaticFilters,
      {}
    );
    this.setState({
      filtersApplied: {},
      listOptions: options.list,
      filterOptions: options.filter,
    });
  };
  handleListChange(event, value, fieldName, all, options) {
    let localValue = Array.isArray(value) ? [...value] : [];
    if (all === "select") {
      Object.values(options).forEach(
        it => !localValue.includes(it.ticker) && localValue.push(it.ticker)
      );
    } else if (all === "deselect") {
      Object.values(options).forEach(
        it =>
          localValue.includes(it.ticker) &&
          localValue.splice(
            localValue.findIndex(el => el === it.ticker),
            1
          )
      );
    } else {
      localValue.includes(event.target.name)
        ? localValue.splice(
            localValue.findIndex(it => it === event.target.name),
            1
          )
        : localValue.push(event.target.name);
    }
    this.props.onChange({ value: localValue, field: fieldName, index: this.props.parentIndex });
  }

  handleCollapseState(filterType) {
    let open = this.state.open;
    if (open === filterType) {
      open = "";
    } else {
      open = filterType;
    }
    this.setState({ open });
  }

  handleOnBlur(e) {
    const currentTarget = e.currentTarget;
    // https://stackoverflow.com/questions/11592966/get-the-newly-focussed-element-if-any-from-the-onblur-event/11592974#11592974
    setTimeout(() => {
      if (!currentTarget.contains(document.activeElement)) {
        this.setState({ open: "" });
      }
    }, 0);
  }

  render() {
    const { value, field, className } = this.props;
    const { fieldName, filterNames, disabled } = field;
    const { listOptions, filtersApplied, filterOptions } = this.state;
    return (
      <ComponentContainer className={className} disabled={disabled}>
        <ResetFilters>
          <span onClick={this.handleResetAllFilters}>
            <FormattedMessage {...messages.resetAllFilters} />
          </span>
        </ResetFilters>
        <FilterContainer>
          {/* Provides initial four filters */}
          {this.originalListOptions[0] &&
            Object.keys(this.originalListOptions[0].filters).map((filterType, idx) => {
              return (
                <StyledCollapseContainer
                  key={filterType}
                  open={this.state.open === filterType}
                  tabIndex={0}
                  onBlur={e => {
                    this.handleOnBlur(e);
                  }}>
                  <CollapseHeader
                    onClick={() => {
                      this.handleCollapseState(filterType);
                    }}>
                    {`${filterNames[idx]}`}{" "}
                    <StyledArrowDown flip={this.state.open === filterType} />{" "}
                  </CollapseHeader>
                  <CollapseBody open={this.state.open === filterType}>
                    <CheckAllControlFilter>
                      <span onClick={() => this.handleAllFiltersChange("select", filterType)}>
                        <FormattedMessage {...messages.selectAll} />
                      </span>
                      <span>{" | "}</span>
                      <span onClick={() => this.handleAllFiltersChange("deselect", filterType)}>
                        {" "}
                        <FormattedMessage {...messages.selectNone} />
                      </span>
                    </CheckAllControlFilter>
                    <CollapseListWrapper>
                      {filterOutDuplicateOptions(filterOptions, filterType).map(option => {
                        return (
                          option.filters[filterType] && (
                            <FormGroup
                              className="form-inline"
                              key={option.filters[filterType].ticker}>
                              <div className="checkbox c-checkbox needsclick">
                                <label className="needsclick ">
                                  <input
                                    disabled={disabled}
                                    type="checkbox"
                                    checked={
                                      !(
                                        filtersApplied[filterType] &&
                                        filtersApplied[filterType].includes(
                                          option.filters[filterType].ticker
                                        )
                                      )
                                    }
                                    className="needsclick"
                                    onChange={event =>
                                      this.handleFilterChange(
                                        event,
                                        filterType,
                                        option.filters[filterType].ticker
                                      )
                                    }
                                  />
                                  <em className="fa fa-check" />
                                  <span>{option.filters[filterType].name}</span>
                                </label>
                              </div>
                            </FormGroup>
                          )
                        );
                      })}
                    </CollapseListWrapper>
                  </CollapseBody>
                </StyledCollapseContainer>
              );
            })}
        </FilterContainer>
        <CheckAllControl>
          <span
            onClick={event =>
              this.handleListChange(event, value, fieldName, "select", listOptions)
            }>
            <FormattedMessage {...messages.selectAll} />
          </span>
          <span>{" | "}</span>
          <span
            onClick={event =>
              this.handleListChange(event, value, fieldName, "deselect", listOptions)
            }>
            {" "}
            <FormattedMessage {...messages.selectNone} />
          </span>
        </CheckAllControl>
        {listOptions.map((val, idx) => (
          <StyledDiv key={val.ticker + idx}>
            <FormGroup className="form-inline">
              <div className="checkbox c-checkbox needsclick">
                <StyledLabel className="needsclick">
                  <input
                    disabled={disabled}
                    type="checkbox"
                    checked={typeof value === "object" && value.some(it => it === val.ticker)}
                    className="needsclick"
                    onChange={event => this.handleListChange(event, value, fieldName)}
                    name={val.ticker}
                  />
                  <em className="fa fa-check" />
                  <span>{val.name}</span>
                </StyledLabel>
              </div>
            </FormGroup>
          </StyledDiv>
        ))}
      </ComponentContainer>
    );
  }
}

SmartSelector.propTypes = {
  field: PropTypes.object,
  formErrors: PropTypes.object,
  formValues: PropTypes.object,
  hasErrors: PropTypes.bool,
  onChange: PropTypes.func,
};

export default SmartSelector;
